import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useWorkspace } from '@/store/workspace';
import { useAuthStore } from '@/store/auth';

interface WorkspaceGuardProps {
  children: React.ReactNode;
}

export function WorkspaceGuard({ children }: WorkspaceGuardProps) {
  const { currentWorkspace, workspaces, isLoading, fetchWorkspaces, enterImpersonation } = useWorkspace();
  const { isSuperadmin } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // A superadmin opening a project from the panel in a new tab arrives with
    // ?assumeWorkspace=<id>&assumeRole=<admin|reader>. Establish the ephemeral
    // assumed context here — before the normal restore/redirect logic runs —
    // so this tab renders the target workspace as that role without ever
    // touching the shared localStorage session (see the store's impersonation
    // handling). ProtectedRoute guarantees auth is initialized above us, so
    // isSuperadmin is already resolved.
    const assumeWorkspaceId = searchParams.get('assumeWorkspace');
    const assumeRoleParam = searchParams.get('assumeRole');
    const wantsImpersonation =
      !!assumeWorkspaceId && (assumeRoleParam === 'admin' || assumeRoleParam === 'reader');

    const checkWorkspaces = async () => {
      try {
        if (wantsImpersonation && isSuperadmin) {
          await enterImpersonation(assumeWorkspaceId!, assumeRoleParam as 'admin' | 'reader');
        } else if (workspaces.length === 0 && !currentWorkspace) {
          await fetchWorkspaces();
        }
      } catch {
        // enterImpersonation / fetchWorkspaces already recorded the error in
        // the store; fall through so the guard can show the appropriate state.
      }
      setHasChecked(true);
    };

    checkWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = run once on mount

  // Show loading while we check workspaces
  if (isLoading || !hasChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-ingesta-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading workspaces...</p>
        </div>
      </div>
    );
  }

  // If no workspace is selected but workspaces exist, redirect to selection
  if (!currentWorkspace && workspaces.length > 0) {
    return <Navigate to="/workspace/select" replace />;
  }

  // If no workspaces exist at all, redirect to creation (onboarding flow)
  if (!currentWorkspace && workspaces.length === 0) {
    return <Navigate to="/workspace/create?onboarding=true" replace />;
  }

  // If we have a current workspace, render the children
  return <>{children}</>;
}